import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCarrito } from '../context/CarritoContext';

const Navbar = () => {
  const { cliente, logout } = useAuth();
  const { totalItems, setCarritoAbierto } = useCarrito();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [menuMovil, setMenuMovil] = useState(false);
  const [menuUsuario, setMenuUsuario] = useState(false);
  const [buscadorMovil, setBuscadorMovil] = useState(false);
  const [termino, setTermino] = useState('');

  // El home y el catálogo ya tienen su propio buscador grande
  const mostrarBuscador = pathname !== '/' && pathname !== '/catalogo';

  const handleLogout = () => {
    logout();
    setMenuUsuario(false);
    navigate('/');
  };

  const buscar = (e) => {
    e.preventDefault();
    const q = termino.trim();
    if (!q) return;
    navigate(`/catalogo?busqueda=${encodeURIComponent(q)}`);
    setTermino('');
    setBuscadorMovil(false);
    setMenuMovil(false);
  };

  // Función normal, NO un componente: así el input no se remonta en cada tecla
  const campoBusqueda = (autoFocus = false) => (
    <form onSubmit={buscar} className="relative w-full">
      <input
        type="search"
        value={termino}
        autoFocus={autoFocus}
        onChange={(e) => setTermino(e.target.value)}
        placeholder="Buscar llantas, cilindros, baterías..."
        aria-label="Buscar productos"
        className="w-full bg-white/10 border border-red-900/50 text-white placeholder-gray-400 text-sm rounded-full pl-10 pr-4 py-2 focus:outline-none focus:border-red-500 focus:bg-white/15 transition-colors"
      />
      <svg xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </form>
  );

  return (
    <nav
      className="sticky top-0 z-50 border-b border-red-900/40 backdrop-blur-md"
      style={{ backgroundColor: 'rgba(18, 8, 8, 0.92)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-4">

          {/* ── Logo ── */}
          <Link to="/" className="flex items-center shrink-0">
            <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-white">
              <em className="not-italic text-red-500">Moto</em>Llantas
            </span>
          </Link>

          {/* ── Buscador desktop ── */}
          {mostrarBuscador && (
            <div className="hidden md:block flex-1 max-w-md">
              {campoBusqueda()}
            </div>
          )}

          {/* ── Links desktop ── */}
          <div className={`hidden ${mostrarBuscador ? 'lg:flex' : 'md:flex'} items-center gap-6 shrink-0 ${mostrarBuscador ? '' : 'ml-auto mr-auto'}`}>
            <Link to="/"
              className="text-gray-300 hover:text-red-400 font-medium text-sm tracking-wide transition-colors duration-200">
              Inicio
            </Link>
            <Link to="/catalogo"
              className="text-gray-300 hover:text-red-400 font-medium text-sm tracking-wide transition-colors duration-200">
              Catálogo
            </Link>
          </div>

          {/* ── Acciones ── */}
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">

            {/* Lupa móvil */}
            {mostrarBuscador && (
              <button
                onClick={() => setBuscadorMovil(prev => !prev)}
                className="md:hidden p-2 text-gray-300 hover:text-red-400 transition-colors duration-200"
                aria-label="Buscar">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}

            {/* Carrito */}
            <button
              onClick={() => setCarritoAbierto(true)}
              className="relative p-2 text-gray-300 hover:text-red-400 transition-colors duration-200"
              aria-label="Ver carrito">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {totalItems}
                </span>
              )}
            </button>

            {/* Usuario */}
            {cliente ? (
              <div className="relative">
                <button
                  onClick={() => setMenuUsuario(prev => !prev)}
                  className="flex items-center gap-2 border border-red-700/60 text-red-400 px-2 sm:px-3 py-1.5 rounded-full text-sm font-medium hover:bg-red-700/20 transition-colors duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="hidden sm:inline">{cliente.nombre.split(' ')[0]}</span>
                </button>

                {menuUsuario && (
                  <div
                    className="absolute right-0 mt-2 w-48 rounded-xl shadow-2xl border border-red-900/40 py-1 z-50"
                    style={{ backgroundColor: '#1a0808' }}>
                    <Link to="/mi-cuenta" onClick={() => setMenuUsuario(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-red-400 hover:bg-red-900/20 transition-colors duration-200">
                      Mi cuenta
                    </Link>
                    <button onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-900/20 transition-colors duration-200">
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login"
                className="border border-red-600 text-red-400 px-3 sm:px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-red-600 hover:text-white transition-all duration-200 tracking-wide">
                Ingresar
              </Link>
            )}

            {/* Hamburguesa */}
            <button onClick={() => setMenuMovil(prev => !prev)}
              className={`${mostrarBuscador ? 'lg:hidden' : 'md:hidden'} p-2 text-gray-300 hover:text-red-400 transition-colors duration-200`}
              aria-label="Abrir menú">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Buscador móvil desplegable ── */}
        {mostrarBuscador && buscadorMovil && (
          <div className="md:hidden pb-3">
            {campoBusqueda(true)}
          </div>
        )}

        {/* ── Menú móvil ── */}
        {menuMovil && (
          <div className={`${mostrarBuscador ? 'lg:hidden' : 'md:hidden'} border-t border-red-900/40 py-3`}>
            <Link to="/" onClick={() => setMenuMovil(false)}
              className="block px-4 py-2 text-sm text-gray-300 hover:text-red-400 transition-colors duration-200">
              Inicio
            </Link>
            <Link to="/catalogo" onClick={() => setMenuMovil(false)}
              className="block px-4 py-2 text-sm text-gray-300 hover:text-red-400 transition-colors duration-200">
              Catálogo
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;